import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'

import { useToastContext } from '../../../../../../../../contexts'
import {
  DeliveryForecast,
  DeliveryForecastItemSplit,
  PatchRequest,
  WebEdiDeliveryForecastDto,
} from '../../../../../../../../data/models'
import { getHandleableError } from '../../../../../../../../utils/errors/isXiaError'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../../../utils/helper/validateFormikFields'
import { useConfigContext } from '../../../../../../../ui-config'
import {
  requestPatchDeliveryForecastsById,
  requestPostSplitItem,
} from '../../../../../../data'
import { AddSplitItemView } from './AddSplitItem.view'

interface Props {
  deliveryForecastId: string
  itemId: string
  unit: string | undefined
  items: DeliveryForecastItemSplit[]
}

export interface FormValues {
  confirmationDeliveryDate: DeliveryForecastItemSplit['ConfirmationDeliveryDate']
  confirmationDeliveryQuantity?: DeliveryForecastItemSplit['ConfirmationDeliveryQuantity']
}

export type FormFieldName = keyof FormValues

export const AddSplitItemContainer: React.FC<Props> = ({
  deliveryForecastId,
  itemId,
  unit,
  items,
}) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language
  const { uiConfig: config } = useConfigContext()
  const [visible, setVisible] = useState(false)

  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState(false)

  const disabledDates: Date[] =
    items &&
    items
      .map(item =>
        item.ConfirmationDeliveryDate
          ? new Date(item.ConfirmationDeliveryDate)
          : null,
      )
      .filter((date): date is Date => date !== null)

  // ************************************************************
  // formik
  // ************************************************************
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      confirmationDeliveryDate: undefined,
      confirmationDeliveryQuantity: undefined,
    },
    validate: formData => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          // confirmationDeliveryDate
          fieldName: 'confirmationDeliveryDate',
          config: config.deliveryForecast.addSplitItem.confirmationDeliveryDate,
        },
        {
          // confirmationDeliveryQuantity
          fieldName: 'confirmationDeliveryQuantity',
          config:
            config.deliveryForecast.addSplitItem.confirmationDeliveryQuantity,
        },
      ]

      validateFormikFields(fieldsToValidate, formData, errors)

      return errors
    },
    onSubmit: async formData => {
      // TODO: onsubmit überall gleich aufbauen
      // deactivate the buttons
      setIsRequestRunning(true)

      try {
        // close the dialog and reset
        handleClose()

        // submit form
        await submitForm(formData)

        // mutate()
        // globalMutate(`/OrderResponses/${id}/Items`)
        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t(
            'deliveryForecast.deliveryScheduleDetails.splits.table.add.dialog.add.messages.success.summary',
          ),
          detail: t(
            'deliveryForecast.deliveryScheduleDetails.splits.table.add.dialog.add.messages.success.detail',
            {
              deliveryDate: new Date(
                formData.confirmationDeliveryDate!,
              ).toLocaleDateString(locale),
              quantity: formData.confirmationDeliveryQuantity,
            },
          ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t(
            'deliveryForecast.deliveryScheduleDetails.splits.table.add.dialog.add.messages.failure.summary',
          ),
          detail: errorMessage,
        })
      } finally {
        setIsRequestRunning(false)
      }
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  // submit form
  const submitForm = async (formData: FormValues) => {
    // Change ConfirmationCode to modified because we added a new one
    const newConfirmationCode: DeliveryForecast['ConfirmationCode'] = 'MODIFIED'
    const patchData: PatchRequest<WebEdiDeliveryForecastDto<DeliveryForecast>> =
      [
        {
          op: 'replace',
          path: '/Payload/ConfirmationCode',
          value: newConfirmationCode,
        },
      ]

    requestPatchDeliveryForecastsById(deliveryForecastId, patchData)

    // Create new split item
    const newSplitItem: DeliveryForecastItemSplit = {
      BuyerDeliveryQuantity: 0,
      Status: 'ADDED',
      BuyerDeliveryDate: formData.confirmationDeliveryDate,
      ConfirmationDeliveryDate: formData.confirmationDeliveryDate,
      ConfirmationDeliveryQuantity: formData.confirmationDeliveryQuantity,
      Unit: unit,
    }
    await requestPostSplitItem(deliveryForecastId, itemId, newSplitItem)
  }

  // handleClose
  const handleClose = () => {
    formik.resetForm()
    setVisible(false)
  }

  return (
    <AddSplitItemView
      deliveryForecastId={deliveryForecastId}
      visible={visible}
      setVisible={setVisible}
      formik={formik}
      isFormFieldValid={isFormFieldValid}
      getFormErrorMessage={getFormErrorMessage}
      handleClose={handleClose}
      isRequestRunning={isRequestRunning}
      disabledDates={disabledDates}
    />
  )
}
